/**
 * Study Protocol API service. SDK for GET (typed response), api helper for writes (no requestBody
 * schemas yet).
 */

import { uploadFileToS3 } from "../utils/uploadFileToS3"
import { sdk, sdkCall } from "./sdk"

export type {
  ProtocolData,
  ProtocolVisit,
  ProtocolPeriod,
  ProtocolAssessment,
  StudyProtocol,
} from "@/generated/sdk"

import type { ProtocolUploadResponse, StudyProtocol, ProtocolData } from "@/generated/sdk"

export const studyProtocolApiService = {
  getProtocol: async (studyId: string): Promise<StudyProtocol | null> => {
    return sdkCall(sdk.getProtocol({ studyId }))
  },

  createProtocol: async (studyId: string, data: ProtocolData): Promise<StudyProtocol> => {
    return sdkCall(sdk.postProtocol({ studyId, createProtocolRequest: { data } }))
  },

  updateProtocol: async (
    studyId: string,
    data: ProtocolData,
    submit: boolean = false,
  ): Promise<StudyProtocol> => {
    return sdkCall(sdk.putProtocol({ studyId, updateProtocolRequest: { data, submit } }))
  },

  /**
   * Upload a protocol PDF via presigned URL (supports files up to 50 MB).
   *
   * Flow: presign -> direct S3 upload -> confirm with backend.
   */
  uploadProtocol: async (
    studyId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<ProtocolUploadResponse> => {
    const presign = await sdkCall(
      sdk.presignDocumentUpload({
        studyId,
        presignUploadRequest: { file_name: file.name, upload_type: "protocol" },
      }),
    )

    await uploadFileToS3(presign.upload_url!, file, presign.content_type!, onProgress)

    return sdkCall(
      sdk.uploadProtocol({
        studyId,
        uploadProtocolFileRequest: { upload_id: presign.upload_id!, file_name: file.name },
      }),
    )
  },
}
